import { useEffect, useRef, useCallback } from 'react';
import { RTDBListenerManager, desanitizeConversationId } from '@/lib/firebase/rtdb';
import { playAlertIfBackground } from '@/lib/utils/alert-sound';
import { useAppStore } from '@/stores/app-store';
import { useAuthStore, getEffectiveUid } from '@/stores/auth-store';
import { useConversationStore, parseActiveFlow, parseOptOut } from '@/stores/conversation-store';
import { useUIStore } from '@/stores/ui-store';
import { useModuleStore } from '@/stores/module-store';
import { useNotificationStore } from '@/stores/notification-store';
import { useMessageStore } from '@/stores/message-store';
import { useAppUsersStore } from '@/stores/app-users-store';
import { dbg } from '@/lib/debug-logger';
import type { RTDBEventValue, RTDBListenerConfig } from '@/types/rtdb';
import type { Message, MessageStatus } from '@/types/messaging';
import { processWhatsAppCallWebhook } from '@/hooks/use-whatsapp-call-listener';
import { readScopedCache } from '@/lib/cache/scoped-cache';
import { IS_APPCURSOR_CALL_HOST } from '@/hooks/use-appcursor-call-bridge';

/** Simple dedup: skip events we processed in the last 2 seconds */
const recentEvents = new Map<string, number>();
function isDuplicate(key: string): boolean {
  const now = Date.now();
  // Purge old entries
  for (const [k, t] of recentEvents) {
    if (now - t > 2000) recentEvents.delete(k);
  }
  if (recentEvents.has(key)) return true;
  recentEvents.set(key, now);
  return false;
}

export function useRealtimeListeners() {
  const managerRef = useRef<RTDBListenerManager | null>(null);

  const extensionName = useAppStore((s) => s.extensionName);
  const appCodeTPA = useAppStore((s) => s.appCodeTPA);
  const user = useAuthStore((s) => s.user);
  const selectedAdminUserId = useAuthStore((s) => s.selectedAdminUserId);
  const tokenClaimsVersion = useAuthStore((s) => s.tokenClaimsVersion);
  const tpaAuthenticated = useModuleStore((s) => s.tpaAuthenticated);

  const handleEvent = useCallback(
    (rawConversationId: string, event: RTDBEventValue) => {
      if (!event || !event.c) return;

      // Twilio: skip RT events that are more than 10 seconds old. On page
      // load the RTDB listener replays cached values from the last session
      // (a single `.set()` + next `.set()` deliver as two value changes),
      // which spams [RT raw] with events we already processed earlier.
      // `event.t` is the server timestamp written by ULGEBRA_INBOX_* helpers.
      // schedule-task is exempt: a reminder that fired while the tab was
      // suspended is still worth surfacing when the connection replays it.
      if (appCodeTPA === 'twilio' && event.c !== 'schedule-task') {
        const eventTime = typeof event.t === 'number' ? event.t : 0;
        if (eventTime && Date.now() - eventTime > 10_000) {
          return;
        }
      }

      // DEBUG: log every raw RT event so we can count events per message
      console.log('[RT raw]', rawConversationId, event);

      const conversationId = desanitizeConversationId(
        rawConversationId,
        appCodeTPA || ''
      );

      dbg.rtdbEvent(event.c, rawConversationId, conversationId, { a: event.a, b: event.b });

      // Dedup — both old and new listeners can fire for the same event
      // Include event.b for msg-status so different statuses (sent→delivered→read) aren't deduped.
      // `event.b` is an OBJECT for schedule-task, and template-stringing it gave
      // "[object Object]" — collapsing every reminder push on a conversation into
      // one key, so a second one inside the 2s window (cancel-then-cancel, or a
      // fire immediately followed by a reschedule) was dropped before the store's
      // own finer-grained dedup could see it.
      const dedupPart =
        event.b && typeof event.b === 'object'
          ? `${JSON.stringify(event.b)}:${(event.z as { eventId?: string } | undefined)?.eventId ?? ''}`
          : (event.b ?? '');
      const dedupKey = `${conversationId}:${event.c}:${event.a}:${dedupPart}`;
      if (isDuplicate(dedupKey)) return;

      const store = useConversationStore.getState();
      const currentView = useUIStore.getState().activeView;
      const currentActiveConv = store.activeConversation;

      switch (event.c) {
        case 'msg-add': {
          // Server RTDB event: { a: messageSid, b: status, c: "msg-add", z?: rawPayload }
          // For WhatsApp, event.z contains the full message payload — convert directly.
          // For other integrations, fetch by SID from Firestore/API.

          const tpaModule = useModuleStore.getState().tpaModule;
          const messageSid = event.a;
          const isActiveChat = currentActiveConv?.id === conversationId;

          // Check if this message already exists (e.g. we sent it optimistically)
          const alreadyExists = !!messageSid && store.messages.some((m) => m.id === messageSid);

          // UA-authored docs — notes, pinned notes, system messages — exist only
          // in Firestore. Both branches below resolve unknown ids through
          // tpaModule.getMessageById(), which for Twilio means
          // GET /Messages/ua-note-1785501631613.json — a guaranteed 404 that
          // costs a proxied round trip on every note added.
          // Nothing to fetch when this tab already rendered it (the author's own
          // optimistic copy); otherwise refresh from Firestore, which is where
          // the doc actually is.
          const zPayloadRaw = event.z as Record<string, unknown> | undefined;
          const isUaAuthoredEvent =
            (!!messageSid && messageSid.startsWith('ua-note')) ||
            zPayloadRaw?._uaOriginService === 'ua-msg-system' ||
            zPayloadRaw?.status === 'note-added' ||
            (typeof zPayloadRaw?.type === 'string' && zPayloadRaw.type.includes('--note'));

          if (isUaAuthoredEvent) {
            if (!alreadyExists && isActiveChat) store.fetchMessages(conversationId);
            break;
          }

          if (alreadyExists) {
            // Twilio: if the msg-add is for an OUTGOING message that's already
            // rendered (optimistically sent by this tab), skip re-processing.
            // The existing local message is authoritative — the RT event carries
            // the same data, and subsequent msg-status events will advance its
            // status without flicker.
            const zOrigin = event.z && (event.z as Record<string, unknown>)._uaOriginService;
            if (zOrigin === 'twilio') {
              const existingMsg = store.messages.find((m) => m.id === messageSid);
              const isOutgoing = event.b === 'sent' || existingMsg?.direction === 'outbound';
              if (isOutgoing) {
                break;
              }
            }

            // Message already in chat (e.g. sent optimistically). Update with real server data.
            // For WhatsApp/Twilio, convert event.z directly. For others, fetch from Firestore.
            if (event.z && (zOrigin === 'whatsapp' || zOrigin === 'twilio')) {
              const hasConvert = tpaModule && typeof (tpaModule as unknown as { convertRawMessage: unknown }).convertRawMessage === 'function';
              if (hasConvert) {
                const mod = tpaModule as unknown as { convertRawMessage(raw: Record<string, unknown>, convId: string): Message | null };
                const convertMsg = mod.convertRawMessage({ ...(event.z as Record<string, unknown>), id: messageSid }, conversationId);
                if (convertMsg) {
                  store.addOrUpdateMessage(convertMsg, true);

                  // Twilio stale-event guard: when this RT event's message is
                  // older than the conversation's currently-displayed message,
                  // skip BOTH the metadata rewrite and the preview update.
                  // Otherwise lastMessageDirection would flip to the stale
                  // event's direction, causing a tick to render on an incoming
                  // conversation.
                  const msgTimeMs = convertMsg.timestamp instanceof Date
                    ? convertMsg.timestamp.getTime()
                    : new Date(convertMsg.timestamp).getTime();
                  let allowUpdate = true;
                  if (appCodeTPA === 'twilio') {
                    const existingConv = store.conversations.find(c => c.id === conversationId);
                    const existingTimeMs = existingConv?.lastMessageTime
                      ? (existingConv.lastMessageTime instanceof Date
                          ? existingConv.lastMessageTime.getTime()
                          : new Date(existingConv.lastMessageTime).getTime())
                      : 0;
                    if (existingTimeMs && msgTimeMs && msgTimeMs < existingTimeMs) {
                      allowUpdate = false;
                    }
                  }

                  if (allowUpdate) {
                    const isCallMsg = !!convertMsg.metadata?.isCallEvent;
                    const convMeta = store.conversations.find(c => c.id === conversationId)?.metadata || {};
                    store.updateConversation(conversationId, {
                      metadata: {
                        ...convMeta,
                        lastMessageStatus: convertMsg.status,
                        lastMessageDirection: convertMsg.direction,
                        lastMessageIsCall: isCallMsg ? true : false,
                        lastCallEventType: isCallMsg ? convertMsg.metadata?.callEventType as string : null,
                      },
                    } as Partial<Record<string, unknown>>);
                    if (convertMsg.body) {
                      store.prependOrUpdateConversation(conversationId, convertMsg.body, new Date(msgTimeMs || Date.now()), false);
                    }
                  }
                }
              }
              break;
            }

            // Non-WhatsApp fallback — fetch from Firestore
            if (tpaModule && typeof (tpaModule as unknown as { getMessageById: unknown }).getMessageById === 'function' && messageSid) {
              const mod = tpaModule as unknown as { getMessageById(sid: string): Promise<Message | null> };
              mod.getMessageById(messageSid).then((msg) => {
                if (msg) {
                  const fresh = useConversationStore.getState();
                  fresh.addOrUpdateMessage(msg, true);
                  const STATUS_WEIGHT: Record<string, number> = { queued: 0, sent: 1, delivered: 2, read: 3, failed: 4 };
                  const currentMeta = fresh.conversations.find(c => c.id === conversationId)?.metadata || {};
                  const currentWeight = STATUS_WEIGHT[(currentMeta as Record<string, string>).lastMessageStatus] ?? -1;
                  const fetchedWeight = STATUS_WEIGHT[msg.status] ?? -1;
                  if (fetchedWeight > currentWeight) {
                    fresh.updateConversation(conversationId, {
                      metadata: {
                        ...currentMeta,
                        lastMessageStatus: msg.status,
                        lastMessageDirection: msg.direction,
                      },
                    } as Partial<Record<string, unknown>>);
                  }
                }
              }).catch(() => {});
            }
            break;
          }

          const foundInStore = store.conversations.some((c) => c.id === conversationId);
          dbg.rtdbMsgAdd(conversationId, messageSid || '', alreadyExists, foundInStore);

          // System messages (assignee-update, label-update, etc.):
          // 1. event.z._uaOriginService === 'ua-msg-system' (when z payload is present)
          // 2. Message ID is a pure number (timestamp-based) — not a TPA SID (e.g. SM..., MM...)
          // They are stored in Firestore as notes, not in the TPA messaging API — skip TPA fetch.
          const isSystemMessage =
            event.z?._uaOriginService === 'ua-msg-system' ||
            (messageSid && /^\d+$/.test(messageSid));

          if (isSystemMessage) {
            // Only refresh messages if viewing this conversation — no conversation bump, no API call
            if (isActiveChat) {
              store.fetchMessages(conversationId);
            }
            break;
          }

          // Check if this is a call_permission_reply and update conversation metadata
          if (event.z) {
            const zPayload = event.z as Record<string, unknown>;
            const msgs = (zPayload.messages ?? (zPayload.entry as Array<Record<string, unknown>>)?.[0]?.changes?.[0]?.value?.messages) as Array<Record<string, unknown>> | undefined;
            const firstMsg = msgs?.[0];
            const interactive = firstMsg?.interactive as Record<string, unknown> | undefined;
            if (interactive?.type === 'call_permission_reply') {
              const reply = interactive.call_permission_reply as Record<string, unknown> | undefined;
              if (reply) {
                // Store raw Facebook payload — same shape CF writes to Firestore
                const convMeta = store.conversations.find(c => c.id === conversationId)?.metadata || {};
                store.updateConversation(conversationId, {
                  metadata: { ...convMeta, callPermission: reply },
                } as Partial<Record<string, unknown>>);
              }
            }
          }

          // For WhatsApp and Twilio, convert event.z directly — no Firestore fetch needed.
          // For other integrations, fall back to getMessageById.
          const hasConvertRawMessage = tpaModule && typeof (tpaModule as unknown as { convertRawMessage: unknown }).convertRawMessage === 'function';
          let convertedFromZ: Message | null = null;

          const zOriginSvc = event.z && (event.z as Record<string, unknown>)._uaOriginService;
          if (event.z && (zOriginSvc === 'whatsapp' || zOriginSvc === 'twilio') && hasConvertRawMessage) {
            const zPayload = event.z as Record<string, unknown>;
            const mod = tpaModule as unknown as { convertRawMessage(raw: Record<string, unknown>, convId: string): Message | null };
            convertedFromZ = mod.convertRawMessage({ ...zPayload, id: messageSid }, conversationId);
          }

          if (convertedFromZ) {
            const msg = convertedFromZ;

            // Add message to chat if this is the active conversation
            if (isActiveChat) {
              store.addOrUpdateMessage(msg, true);
            }

            // Bump conversation to top with subject
            let subject = msg.body || '';
            if (!subject && msg.attachments && msg.attachments.length > 0) {
              const mediaType = msg.attachments[0].type || 'media';
              subject = `📎 ${mediaType === 'image' ? 'Image' : mediaType === 'video' ? 'Video' : mediaType === 'audio' ? 'Audio' : 'Attachment'} received`;
            }
            if (!subject) subject = 'New message';

            const customerNumber = msg.direction === 'inbound' ? msg.from : msg.to;
            const cleanCustomer = customerNumber?.replace('whatsapp:', '').replace('+', '') || '';
            const isCallMsg = !!msg.metadata?.isCallEvent;
            const msgMetadata: Record<string, unknown> = {
              lastMessageStatus: msg.status,
              lastMessageDirection: msg.direction,
              lastMessageType: msg.metadata?.isTemplate ? 'template' : undefined,
              ...(msg.direction === 'inbound' && { lastInboundTime: new Date(msg.timestamp).getTime() }),
              lastMessageIsCall: isCallMsg ? true : false,
              lastCallEventType: isCallMsg ? msg.metadata?.callEventType as string : null,
            };

            // Twilio: guard against out-of-order RT events. If the conversation
            // already shows a newer message than this event carries, skip the
            // prepend/text update — otherwise a late event would regress the
            // displayed preview. Only outbound messages from other devices or
            // inbound messages that arrive after a reply have this risk.
            const msgTime = msg.timestamp instanceof Date ? msg.timestamp.getTime() : new Date(msg.timestamp).getTime();
            let shouldUpdatePreview = true;
            if (appCodeTPA === 'twilio') {
              const existingConv = store.conversations.find(c => c.id === conversationId);
              const existingTime = existingConv?.lastMessageTime
                ? (existingConv.lastMessageTime instanceof Date
                    ? existingConv.lastMessageTime.getTime()
                    : new Date(existingConv.lastMessageTime).getTime())
                : 0;
              if (existingTime && msgTime && msgTime < existingTime) {
                shouldUpdatePreview = false;
              }
            }

            if (shouldUpdatePreview) {
              store.prependOrUpdateConversation(conversationId, subject, new Date(msgTime || Date.now()), undefined, {
                contactName: cleanCustomer,
                contactPhone: cleanCustomer,
                channel: msg.channel,
                metadata: msgMetadata,
              });
              // Only push the new message direction/status into conversation
              // metadata when we actually adopted this message as the latest.
              // Doing it unconditionally would flip lastMessageDirection to the
              // stale event's direction, causing incoming conversations to
              // incorrectly render an outbound tick.
              store.updateConversation(conversationId, {
                metadata: {
                  ...((store.conversations.find(c => c.id === conversationId)?.metadata) || {}),
                  ...msgMetadata,
                },
              } as Partial<Record<string, unknown>>);
            }

            // If this message has unresolved media (sticker/GIF/image arriving via RT without
            // downloadedMediaURL), trigger the backend to download & store it, then re-fetch.
            const unresolvedMediaId = (msg.metadata as Record<string, unknown> | undefined)?.unresolvedMediaId as string | undefined;
            if (unresolvedMediaId && messageSid) {
              const hasResolve = typeof (tpaModule as unknown as { resolveMediaForRTMessage: unknown }).resolveMediaForRTMessage === 'function';
              if (hasResolve) {
                const contactId = conversationId.split('::')[1] || '';
                const mod = tpaModule as unknown as { resolveMediaForRTMessage(convId: string, contactId: string, msgId: string, mediaId: string): Promise<void> };
                mod.resolveMediaForRTMessage(conversationId, contactId, messageSid, unresolvedMediaId);
              }
            }
          } else {
            // Fallback for non-WhatsApp integrations — fetch from Firestore/API
            const hasGetMessageById = tpaModule && typeof (tpaModule as unknown as { getMessageById: unknown }).getMessageById === 'function';

            if (hasGetMessageById && messageSid) {
              const mod = tpaModule as unknown as { getMessageById(sid: string, convId?: string): Promise<Message | null> };
              mod.getMessageById(messageSid, conversationId).then((msg) => {
                if (msg) {
                  const fresh = useConversationStore.getState();

                  if (isActiveChat) {
                    fresh.addOrUpdateMessage(msg, true);
                  }

                  let subject = msg.body || '';
                  if (!subject && msg.attachments && msg.attachments.length > 0) {
                    const mediaType = msg.attachments[0].type || 'media';
                    subject = `📎 ${mediaType === 'image' ? 'Image' : mediaType === 'video' ? 'Video' : mediaType === 'audio' ? 'Audio' : 'Attachment'} received`;
                  }
                  if (!subject) subject = 'New message';

                  const customerNumber = msg.direction === 'inbound' ? msg.from : msg.to;
                  const cleanCustomer = customerNumber.replace('whatsapp:', '').replace('+', '');
                  const isCallMsg = !!msg.metadata?.isCallEvent;
                  const msgMetadata: Record<string, unknown> = {
                    lastMessageStatus: msg.status,
                    lastMessageDirection: msg.direction,
                    lastMessageType: msg.metadata?.isTemplate ? 'template' : undefined,
                    ...(msg.direction === 'inbound' && { lastInboundTime: new Date(msg.timestamp).getTime() }),
                    lastMessageIsCall: isCallMsg ? true : false,
                    lastCallEventType: isCallMsg ? msg.metadata?.callEventType as string : null,
                  };

                  fresh.prependOrUpdateConversation(conversationId, subject, new Date(), undefined, {
                    contactName: cleanCustomer,
                    contactPhone: cleanCustomer,
                    channel: msg.channel,
                    metadata: msgMetadata,
                  });
                  fresh.updateConversation(conversationId, {
                    metadata: {
                      ...((fresh.conversations.find(c => c.id === conversationId)?.metadata) || {}),
                      ...msgMetadata,
                    },
                  } as Partial<Record<string, unknown>>);
                } else if (isActiveChat) {
                  useConversationStore.getState().fetchMessages(conversationId);
                } else {
                  useConversationStore.getState().prependOrUpdateConversation(conversationId, 'New message', new Date());
                }
              }).catch(() => {
                const fresh = useConversationStore.getState();
                if (isActiveChat) {
                  fresh.fetchMessages(conversationId);
                } else {
                  fresh.prependOrUpdateConversation(conversationId, 'New message', new Date());
                }
              });
            } else if (isActiveChat) {
              store.fetchMessages(conversationId);
            } else {
              store.prependOrUpdateConversation(conversationId, 'New message', new Date());
            }
          }

          playAlertIfBackground(event);
          break;
        }
        case 'msg-status': {
          const rawStatusStr = typeof event.b === 'string' ? event.b : '';
          const messageSid = event.a;
          if (!rawStatusStr || !messageSid) break;

          // The Send Complete panel shows "sent" the moment the provider
          // ACCEPTS a message; real failures arrive here seconds later. Give
          // the panel the update so it stops reporting a failed send as sent.
          // No-op unless a result row carries this message id. Scoped to
          // routemobilewa so every other app's panel behaves exactly as master.
          if (extensionName?.split('for')[0] === 'routemobilewa') {
            useMessageStore.getState().applyDeliveryStatus(messageSid, rawStatusStr);
          }

          // Call-specific statuses — update call message body & metadata instead of regular status
          const CALL_STATUSES = ['RINGING', 'ACCEPTED', 'REJECTED'];
          if (CALL_STATUSES.includes(rawStatusStr.toUpperCase())) {
            const callStatus = rawStatusStr.toUpperCase();
            const isActiveChat = currentActiveConv?.id === conversationId;

            // Determine updated body text (used for both message bubble & conversation subject)
            let updatedBody = '';
            let callEventType = '';
            let callDir = 'outbound';

            if (isActiveChat) {
              const msg = store.messages.find((m) => m.id === messageSid);
              if (msg?.metadata?.isCallEvent) {
                callDir = msg.metadata.callDirection as string;
                const isInbound = callDir === 'inbound';
                updatedBody = msg.body;
                callEventType = msg.metadata.callEventType as string;

                if (callStatus === 'RINGING') {
                  updatedBody = isInbound ? 'Incoming call · Ringing' : 'Outgoing call · Ringing';
                  callEventType = 'ringing';
                } else if (callStatus === 'ACCEPTED') {
                  updatedBody = isInbound ? 'Incoming call · Ongoing' : 'Outgoing call · Ongoing';
                  callEventType = 'accepted';
                } else if (callStatus === 'REJECTED') {
                  updatedBody = isInbound ? 'Call declined' : 'Call declined';
                  callEventType = 'rejected';
                }

                store.addOrUpdateMessage({
                  ...msg,
                  body: updatedBody,
                  metadata: {
                    ...msg.metadata,
                    callEventType,
                    callStatus,
                  },
                }, true);
              }
            } else {
              // Not active chat — still compute display text for conversation subject
              if (callStatus === 'RINGING') { updatedBody = 'Ringing'; callEventType = 'ringing'; }
              else if (callStatus === 'ACCEPTED') { updatedBody = 'Ongoing call'; callEventType = 'accepted'; }
              else if (callStatus === 'REJECTED') { updatedBody = 'Call declined'; callEventType = 'rejected'; }
            }

            // Update conversation list subject + call metadata
            if (updatedBody) {
              const freshState = useConversationStore.getState();
              freshState.prependOrUpdateConversation(conversationId, updatedBody, new Date(), false);
              const conv = freshState.conversations.find(c => c.id === conversationId);
              const convMeta = conv?.metadata || {};
              freshState.updateConversation(conversationId, {
                metadata: {
                  ...convMeta,
                  lastMessageIsCall: true,
                  lastCallEventType: callEventType,
                },
              } as Partial<Record<string, unknown>>);
            }
            break;
          }

          const newStatus = rawStatusStr.toLowerCase() as MessageStatus;

          const isActiveChat = currentActiveConv?.id === conversationId;

          // Providers can name a message two ways in the same breath. Route
          // Mobile's status callback quotes its own request id in `a`, while a
          // message loaded from their history API is keyed by the WhatsApp id —
          // so a status can be about a message we hold under a different name.
          // The raw callback travels in `z` and carries both, so when `a` finds
          // nothing, look for the other id before giving up. Matching on the
          // WhatsApp id here rather than adopting it as the message id keeps
          // reply quoting working, which resolves against that same id.
          const statusPayload = event.z as
            | { statuses?: Array<{ id?: string; message_id?: string }> }
            | undefined;
          const alternateSid = statusPayload?.statuses?.[0]?.message_id;
          const matchedBySid = store.messages.some((m) => m.id === messageSid);
          const matchedByAlternate =
            !matchedBySid &&
            !!alternateSid &&
            store.messages.some((m) => m.id === alternateSid);
          const effectiveSid = matchedByAlternate && alternateSid ? alternateSid : messageSid;
          const messageExists = matchedBySid || matchedByAlternate;

          if (messageExists) {
            // Message is in our loaded messages — update its status
            store.updateMessageStatus(effectiveSid, newStatus);
          } else {
            // Message doesn't exist locally — could be from another tab/device/user,
            // or for a completely different conversation (another user sent msg,
            // customer read a msg in another conversation, etc.).
            // Always fetch so we can update the conversation list metadata.
            const tpaModule = useModuleStore.getState().tpaModule;
            if (tpaModule && typeof (tpaModule as unknown as { getMessageById: unknown }).getMessageById === 'function') {
              const mod = tpaModule as unknown as { getMessageById(sid: string): Promise<Message | null> };
              mod.getMessageById(messageSid).then((msg) => {
                if (msg) {
                  const fresh = useConversationStore.getState();
                  const freshActiveConv = fresh.activeConversation;

                  // Only add to chat messages if this is the active conversation
                  if (freshActiveConv?.id === conversationId) {
                    fresh.addOrUpdateMessage(msg, true);
                  }

                  // Update conversation list subject & metadata ONLY if this is
                  // the latest message. Multiple status events can arrive out of
                  // order (e.g. Twilio sends "read" for several msgs at once) —
                  // we must not replace a newer message's subject with an older one.
                  const conv = fresh.conversations.find(c => c.id === conversationId);
                  const convMeta = conv?.metadata || {};
                  const msgTime = new Date(msg.timestamp).getTime();
                  const existingTime = conv?.lastMessageTime
                    ? new Date(conv.lastMessageTime).getTime()
                    : 0;

                  if (msgTime >= existingTime) {
                    // This message is the latest (or newer) — update subject & direction
                    let subject = msg.body || '';
                    if (!subject && msg.attachments && msg.attachments.length > 0) {
                      subject = '📎 Attachment';
                    }
                    if (subject) {
                      fresh.prependOrUpdateConversation(conversationId, subject, new Date(msg.timestamp), false);
                    }
                    fresh.updateConversation(conversationId, {
                      metadata: {
                        ...convMeta,
                        lastMessageStatus: msg.status,
                        lastMessageDirection: msg.direction,
                        lastMessageType: msg.metadata?.isTemplate ? 'template' : undefined,
                      },
                    } as Partial<Record<string, unknown>>);
                  } else {
                    // Older message — only update its status if it advances the weight
                    // (don't touch subject or direction)
                    const STATUS_WT: Record<string, number> = { queued: 0, sent: 1, delivered: 2, read: 3, failed: 4 };
                    const curW = STATUS_WT[(convMeta as Record<string, string>).lastMessageStatus] ?? -1;
                    const msgW = STATUS_WT[msg.status] ?? -1;
                    if (msgW > curW) {
                      fresh.updateConversation(conversationId, {
                        metadata: {
                          ...convMeta,
                          lastMessageStatus: msg.status,
                        },
                      } as Partial<Record<string, unknown>>);
                    }
                  }
                }
              }).catch(() => {});
            }
          }

          // Always update the conversation's last message status in metadata
          const freshState = useConversationStore.getState();
          const convMeta = freshState.conversations.find(c => c.id === conversationId)?.metadata || {};
          const STATUS_WEIGHT: Record<string, number> = { queued: 0, sent: 1, delivered: 2, read: 3, failed: 4 };
          const currentWeight = STATUS_WEIGHT[(convMeta as Record<string, string>).lastMessageStatus] ?? -1;
          const newWeight = STATUS_WEIGHT[newStatus] ?? -1;
          if (newWeight > currentWeight) {
            freshState.updateConversation(conversationId, {
              metadata: {
                ...convMeta,
                lastMessageStatus: newStatus,
              },
            } as Partial<Record<string, unknown>>);
          }
          break;
        }
        case 'conv-update': {
          if (typeof event.b === 'object' && event.b !== null) {
            const updates = event.b as Record<string, unknown>;

            // Map RTDB field names to our Conversation type fields
            const mapped: Record<string, unknown> = {};
            if (updates.subject !== undefined) {
              mapped.contactName = updates.subject;
            }
            if (updates.pinnedMessage !== undefined) {
              mapped.pinnedMessageId = updates.pinnedMessage || null;
            }
            if (updates.label !== undefined) {
              mapped.label = updates.label;
            }
            if (updates.assigneeEmail !== undefined) {
              mapped.assigneeEmail = updates.assigneeEmail || 'unassigned';
            }
            if (updates.latestExistingRecordName !== undefined) {
              mapped.crmRecordName = updates.latestExistingRecordName as string;
            }
            // If only assigneeLastUpdatedTime came without assigneeEmail, treat as unassign
            if (updates.assigneeEmail === undefined && updates.assigneeLastUpdatedTime !== undefined) {
              mapped.assigneeEmail = 'unassigned';
            }
            // Chat Automation flow marker — set while a flow is paused mid-run,
            // cleared (null) when it ends or the agent hits "Stop flow". This is
            // the ONLY realtime signal that unlocks the composer, so it must map
            // to `activeFlow` (like the Firestore refetch paths do) — otherwise it
            // silently lands in metadata and the banner never clears live.
            if ('_activeFlow' in updates) {
              mapped.activeFlow = parseActiveFlow(updates._activeFlow) ?? undefined;
            }
            // KAN-155: the customer sent STOP / START — locks or unlocks the
            // composer live.
            if ('optOut' in updates) {
              mapped.optOut = parseOptOut(updates.optOut) ?? undefined;
            }
            // Team-wide "Mark as unread" flag. This event is the CF's own
            // echo, so it is the fastest (and causally ordered) signal that a
            // teammate flagged or opened the chat — map it, or the dot only
            // ever arrives via a Firestore refresh or reload.
            if ('__markedUnread' in updates) {
              const conv = store.conversations.find((c) => c.id === conversationId);
              if (updates.__markedUnread === true) {
                if (conv && conv.id !== store.activeConversation?.id) {
                  mapped.unreadCount = Math.max(conv.unreadCount || 0, 1);
                  // The echo also reaches the tab that marked it, where the
                  // row already carries the placeholder 1 — keep its dot.
                  mapped.markedUnread = !!conv.markedUnread || (conv.unreadCount || 0) === 0;
                }
              } else if (conv?.markedUnread) {
                mapped.markedUnread = false;
                if ((conv.unreadCount || 0) <= 1) mapped.unreadCount = 0;
              }
            }
            // Merge any remaining fields into metadata
            const metaKeys = Object.keys(updates).filter(
              (k) => !['subject', 'pinnedMessage', 'label', 'assigneeEmail', 'latestExistingRecordName', '_activeFlow', 'optOut', '__markedUnread'].includes(k)
            );
            if (metaKeys.length > 0) {
              const conv = store.conversations.find((c) => c.id === conversationId);
              const existingMeta = conv?.metadata || {};
              const newMeta: Record<string, unknown> = { ...existingMeta };
              metaKeys.forEach((k) => { newMeta[k] = updates[k]; });
              mapped.metadata = newMeta;
            }

            dbg.rtdbConvUpdate(conversationId, updates, mapped);
            if (Object.keys(mapped).length > 0) {
              store.updateConversation(conversationId, mapped);
            }

            // If assignee changed, pre-fetch user details for the avatar
            if (updates.assigneeEmail && typeof updates.assigneeEmail === 'string' && updates.assigneeEmail !== 'unassigned') {
              const appUsersStore = useAppUsersStore.getState();
              if (!appUsersStore.userDetailsCache[updates.assigneeEmail]) {
                appUsersStore.fetchUserDetails(updates.assigneeEmail);
              }
            }
          }
          break;
        }
        case 'call-event': {
          // WhatsApp Calling API webhook — call connect, status, or terminate
          // The full webhook payload is in event.z
          if (event.z) {
            processWhatsAppCallWebhook(event.z);
          }
          break;
        }
        case 'schedule-task': {
          // Server push when a chat reminder fires (or is set/cancelled from
          // another device). Legacy: handleRealTimeUpdateScheduledEvents.
          store.handleReminderEvent(event);
          break;
        }
        default:
          break;
      }
    },
    [appCodeTPA, extensionName]
  );

  // Attach listeners once when authenticated — stays on for the session lifetime.
  // The parent-level listener receives events for ALL conversations, so there is
  // no need to tear down / reattach when switching views or conversations.
  useEffect(() => {
    // The hidden AppCursor call host only needs the Twilio Device — message
    // listeners here would play alert sounds in every CRM tab.
    if (IS_APPCURSOR_CALL_HOST) return;
    if (!extensionName || !user || !tpaAuthenticated) {
      managerRef.current?.cleanup();
      return;
    }

    if (!managerRef.current) {
      managerRef.current = new RTDBListenerManager();
    }
    const manager = managerRef.current;

    const effectiveUid = getEffectiveUid() || '';
    const config: RTDBListenerConfig = {
      extensionName,
      extensionNameBase: extensionName.split('for')[0],
      userId: effectiveUid,
      orgId: readScopedCache('ua_cache_appOrgId') || null,
      isWhatsApp: extensionName.split('for')[0] === 'whatsapp',
    };

    manager.attach(config, handleEvent);
  }, [
    extensionName,
    user,
    selectedAdminUserId,
    tokenClaimsVersion,
    tpaAuthenticated,
    handleEvent,
  ]);

  // Full cleanup on unmount
  useEffect(() => {
    return () => {
      managerRef.current?.cleanup();
      managerRef.current = null;
    };
  }, []);

  // Start RTDB unread notification count listener
  // Use impersonatedUserId for admin debugging, skip selectedAdminUserId
  // (shared users see their own notifications, not the admin's)
  const impersonatedUserId = useAuthStore((s) => s.impersonatedUserId);
  useEffect(() => {
    const uid = impersonatedUserId ?? user?.uid;
    if (IS_APPCURSOR_CALL_HOST || !extensionName || !uid) return;
    useNotificationStore.getState().startListeningUnreadCount(uid, extensionName);

    // Re-attach FCM on page load if notifications were previously enabled
    // (the onFCMMessage listener is lost on reload). reattachFCM verifies the
    // token is still registered server-side first — a token disabled from
    // another device must NOT re-enroll itself here.
    useNotificationStore.getState().reattachFCM();

    return () => {
      useNotificationStore.getState().stopListeningUnreadCount();
    };
  }, [extensionName, user?.uid, impersonatedUserId]);
}
